import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db, onAuthReady } from "../../firebase";
import styles from "./login.module.css";
import { ThemeContext } from "../../context/ThemeContext";

export default function SignUp() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  const [theme, setTheme] = useState(isDarkMode);

  useEffect(() => {
    setTheme(isDarkMode);
  }, [isDarkMode]);

  const signUpHandler = async () => {
    if (!acceptedTerms) {
      alert("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }

    try {
      // Use Firebase authentication directly
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Write user profile to clientUsers collection in Firestore
      const now = new Date().toISOString();
      await setDoc(doc(db, "clientUsers", user.uid), {
        uid: user.uid,
        createdAt: now,
        acceptTerms: true,
        acceptedTermsTimestamp: now,
      });
      
      // Wait for auth state to be ready before navigating
      await onAuthReady;
      
      // Firebase SDK now manages the session with browserSessionPersistence
      navigate("/");
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Signup error:", error);
      }
      
      // Show user-friendly error message
      let errorMessage = "Signup failed. Please try again.";
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = "An account with this email already exists.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Password is too weak. Please use a stronger password.";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Invalid email address.";
      }
      
      alert(errorMessage);
    }
  };

  return (
    <div
      className={`${styles["login-form--container"]} ${
        theme ? styles.dark : styles.light
      }`}
    >
      <div className={styles["login-form"]}>
        <div className={styles.centeredContainer}>
          <h2 className={styles.centeredText} style={{ paddingLeft: "0px" }}>
            SIGN UP
          </h2>
        </div>
        <input
          className={styles.inputbox}
          type="email"
          placeholder="Email"
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className={styles.inputbox}
          type="password"
          placeholder="Password"
          onChange={(e) => setPassword(e.target.value)}
        />
        <label
          htmlFor="accept-terms"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "4px",
            marginTop: "4px",
            marginBottom: "8px",
            margin: 0,
            lineHeight: 1.4,
          }}
        >
          <input
            id="accept-terms"
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            style={{ width: "16px", height: "16px", marginTop: "4px", flexShrink: 0, cursor: "pointer" }}
          />
          <span>
            I agree to the
            {" "}
            <a
              href="https://linesol.online/legal/terms-dashboard/"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Service
            </a>
            {" "}and{" "}
            <a
              href="https://linesol.online/legal/privacy-dashboard/"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            {" "}for the Linesol Dashboard.
          </span>
        </label>
        <button
          onClick={() => signUpHandler(email, password)}
          disabled={!acceptedTerms}
          style={{ opacity: !acceptedTerms ? 0.6 : 1, cursor: !acceptedTerms ? "not-allowed" : "pointer" }}
        >
          Sign Up
        </button>
        <p>
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
}
